<script setup>
import { Button } from "@/components/ui/button";
import { Check } from "lucide-vue-next";
import AnimatedSection from "./AnimatedSection.vue";

const plans = [
  {
    name: "Básico",
    description: "Para pequeños equipos que necesitan visibilidad esencial en teletrabajo.",
    price: "Desde 9 €",
    period: "/usuario/mes",
    features: [
      "Monitorización básica",
      "Informes estándar",
      "Soporte por email",
    ],
    highlighted: false,
  },
  {
    name: "Pro",
    description: "Para empresas en crecimiento que requieren seguridad, análisis avanzado y más control.",
    price: "Desde 19 €",
    period: "/usuario/mes",
    features: [
      "Todas las funciones del plan Básico",
      "Alertas avanzadas",
      "IA para detección de anomalías",
      "Soporte prioritario",
    ],
    highlighted: true,
  },
  {
    name: "Enterprise",
    description: "Para organizaciones con requisitos avanzados de seguridad, cumplimiento e integración.",
    price: "Precio a medida",
    period: "",
    features: [
      "Integraciones personalizadas",
      "Soporte dedicado",
      "Funcionalidades avanzadas de cumplimiento",
      "Acompañamiento en despliegue y adopción",
    ],
    highlighted: false,
  },
];
</script>

<template>
  <section id="pricing" class="py-24 bg-secondary/20 relative scroll-mt-20">
    <!-- Background pattern -->
    <div class="absolute inset-0 cyber-grid opacity-30" />
    
    <div class="container mx-auto px-6 relative z-10">
      <!-- Section header -->
      <AnimatedSection animation="fade-up" class="text-center mb-16 max-w-2xl mx-auto">
        <h2 class="text-3xl md:text-4xl font-bold text-foreground mb-6">
          Planes pensados para tu organización
        </h2>
        <p class="text-lg text-muted-foreground">
          Elige el plan que mejor se adapta al tamaño y madurez de tu equipo. Escala cuando lo necesites, sin fricciones.
        </p>
      </AnimatedSection>

      <!-- Pricing cards -->
      <div class="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto">
        <AnimatedSection 
          v-for="(plan, index) in plans"
          :key="index"
          animation="fade-up"
          :delay="index * 150"
        >
          <div 
            :class="[
              'relative rounded-xl p-6 lg:p-8 border transition-all duration-300 h-full',
              plan.highlighted 
                ? 'bg-card border-accent/40 cyan-border-glow scale-105 z-10' 
                : 'bg-card/50 border-border/40 hover:border-border/60'
            ]"
          >
            <!-- Highlighted badge -->
            <div v-if="plan.highlighted" class="absolute -top-3 left-1/2 -translate-x-1/2">
              <span class="px-4 py-1 text-xs font-medium bg-accent text-accent-foreground rounded-full">
                Recomendado
              </span>
            </div>

            <!-- Plan name -->
            <h3 class="text-xl font-bold text-foreground mb-2">
              {{ plan.name }}
            </h3>

            <!-- Description -->
            <p class="text-sm text-muted-foreground mb-6 min-h-[48px]">
              {{ plan.description }}
            </p>

            <!-- Price -->
            <div class="mb-6">
              <span class="text-3xl font-bold text-foreground">
                {{ plan.price }}
              </span>
              <span class="text-sm text-muted-foreground">
                {{ plan.period }}
              </span>
            </div>

            <!-- CTA Button -->
            <Button 
              :variant="plan.highlighted ? 'hero-primary' : 'titanium'" 
              class="w-full mb-6"
            >
              {{ plan.name === "Enterprise" ? "Contactar" : "Empezar ahora" }}
            </Button>

            <!-- Features -->
            <ul class="space-y-3">
              <li v-for="(feature, i) in plan.features" :key="i" class="flex items-start gap-3 text-sm">
                <Check :class="['w-4 h-4 mt-0.5 flex-shrink-0', plan.highlighted ? 'text-accent' : 'text-muted-foreground']" />
                <span class="text-muted-foreground">{{ feature }}</span>
              </li>
            </ul>
          </div>
        </AnimatedSection>
      </div>
    </div>
  </section>
</template>
